import * as React from "react";
import {
  MenuItem,
  Select,
  InputLabel,
  SelectChangeEvent,
  IconButton,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { FilterRowItem } from "./styles";
import { ReactComponent as IconCancelRound } from "../../assets/svg/icon-x-round.svg";
import { getValueAsEvent } from "./filters";

interface Props {
  title: string;
  filterKey: string;
  boxStyle?: any;
  disabled?: boolean;
  options: Array<string>;
  placeholder?: string;
  optionsObjArray?: Array<{ id: string; name: string }>;
  onChange?: (event: SelectChangeEvent<string>) => void;
  updateSearch: (key: string, event: SelectChangeEvent<string>) => void;
}

export function SingleFilter({
  title,
  filterKey,
  boxStyle,
  options,
  optionsObjArray,
  onChange,
  placeholder,
  disabled,
  updateSearch,
}: Props) {
  const [searchParams] = useSearchParams();

  const getPlaceholder = () => {
    if (!placeholder) return null;
    return (
      <MenuItem value={placeholder} disabled selected>
        {placeholder}
      </MenuItem>
    );
  };

  const getMenuOptions = () => {
    if (optionsObjArray) {
      return optionsObjArray?.map((option) => (
        <MenuItem value={option.id} key={option.id}>
          {option.name}
        </MenuItem>
      ));
    } else if (!options) return null;
    return options?.map((option) => (
      <MenuItem value={option} key={option}>
        {option}
      </MenuItem>
    ));
  };

  const onFilterValueChange = (event: SelectChangeEvent<string>) => {
    if (onChange) onChange(event);
    else updateSearch(filterKey, event);
  };

  const clearValue = () => {
    onFilterValueChange(getValueAsEvent());
  };

  const value = React.useMemo(() => {
    const val = searchParams.get(filterKey) || "";
    if (!optionsObjArray) return val;
    return optionsObjArray.find((e) => e.id.toString() === val)?.name || "";
  }, [searchParams, optionsObjArray]);

  return (
    <FilterRowItem isDisabled={disabled}>
      <InputLabel>{title}</InputLabel>
      <Select
        value={value || placeholder || ""}
        onChange={onFilterValueChange}
        renderValue={(val) => (val.length ? val : title)}
        sx={{
          ...(boxStyle || {}),
          minWidth: "140px",
          width:"100%",
          "& .MuiSelect-iconOutlined": { display: value ? "none" : "" },
        }}
        endAdornment={
          <IconButton
            sx={{ display: value ? "" : "none", padding: 0 }}
            onClick={clearValue}
          >
            <IconCancelRound />
          </IconButton>
        }
      >
        {getPlaceholder()}
        {getMenuOptions()}
      </Select>
    </FilterRowItem>
  );
}
